import { useId, useState } from 'react';
import { TermsSignerRole } from '../../../types';

/** Spec 071 — the typed name is validated on its trimmed length but sent exactly as typed. */
export const SIGNER_NAME_MIN = 2;
export const SIGNER_NAME_MAX = 150;

const NAME_SEPARATORS = new Set([' ', '-', "'", '\u2019', '.']);
const TATWEEL = '\u0640';
const LATIN_OR_ARABIC = /^[\p{Script=Latin}\p{Script=Arabic}]$/u;
const LETTER = /^\p{L}$/u;
const MARK = /^[\p{Mn}\p{Mc}]$/u;
const MARK_SCRIPT = /^[\p{Script=Latin}\p{Script=Arabic}\p{Script=Inherited}]$/u;

/**
 * Spec 071 QA F1, decision A — the same rule as garages: Latin or Arabic letters (combining marks right after
 * a letter, tatweel), space, hyphen, apostrophe (' and ’) and period, with at least one letter. Checked on the
 * NFC form only; the name is still sent exactly as typed. The server also refuses letters the PDF font cannot
 * draw, which comes back as a `signature.fullName` field error.
 */
export const isSignerNameCharsetValid = (fullName: string): boolean => {
  let anyLetter = false;
  let markAllowed = false;
  for (const ch of fullName.normalize('NFC')) {
    if (NAME_SEPARATORS.has(ch)) {
      markAllowed = false;
    } else if (ch === TATWEEL || (LETTER.test(ch) && LATIN_OR_ARABIC.test(ch))) {
      anyLetter = true;
      markAllowed = true;
    } else if (!(markAllowed && MARK.test(ch) && MARK_SCRIPT.test(ch))) {
      return false;
    }
  }
  return anyLetter;
};

const isSignerNameLengthValid = (fullName: string): boolean => {
  const len = fullName.trim().length;
  return len >= SIGNER_NAME_MIN && len <= SIGNER_NAME_MAX;
};

export const isSignerNameValid = (fullName: string): boolean =>
  isSignerNameLengthValid(fullName) && isSignerNameCharsetValid(fullName);

export const isSignatureValid = (
  fullName: string,
  signerRole: TermsSignerRole | null,
  consent: boolean,
): boolean => isSignerNameValid(fullName) && signerRole !== null && consent;

export interface TermsSignatureLabels {
  title: string;
  fullName: string;
  fullNamePlaceholder: string;
  fullNameError: string;
  /** Shown when the name uses characters outside Latin/Arabic letters and space - ' ’ . (QA F1). */
  fullNameCharset: string;
  role: string;
  roleOwner: string;
  roleAuthorised: string;
  roleError: string;
  consentError: string;
  /** Already interpolated with the signer's email; empty hides the note. */
  emailNote: string;
}

interface TermsSignatureBlockProps {
  fullName: string;
  signerRole: TermsSignerRole | null;
  consent: boolean;
  /** The server's consent wording in the portal language, rendered verbatim. */
  consentText: string;
  /** Show every invalid field's error now (e.g. after the server refused the signature). */
  forceErrors?: boolean;
  /** The server refused this name (`signature.fullName`) although it passes the client rule. */
  serverNameRejected?: boolean;
  disabled?: boolean;
  onFullNameChange: (value: string) => void;
  onSignerRoleChange: (role: TermsSignerRole) => void;
  onConsentChange: (value: boolean) => void;
  labels: TermsSignatureLabels;
}

/**
 * Spec 071 Flow A step 2 — the clickwrap signature inside "Ready to submit": typed full name,
 * signer role and the consent checkbox carrying the exact server wording. Errors show once a
 * field has been left (or when `forceErrors` is set) and are tied to their control with
 * `aria-describedby`.
 */
export default function TermsSignatureBlock({
  fullName,
  signerRole,
  consent,
  consentText,
  forceErrors = false,
  serverNameRejected = false,
  disabled = false,
  onFullNameChange,
  onSignerRoleChange,
  onConsentChange,
  labels,
}: TermsSignatureBlockProps) {
  const uid = useId();
  const ids = {
    title: `${uid}-title`,
    name: `${uid}-name`,
    nameError: `${uid}-name-error`,
    roleLegend: `${uid}-role`,
    roleError: `${uid}-role-error`,
    consent: `${uid}-consent`,
    consentError: `${uid}-consent-error`,
    note: `${uid}-note`,
  };
  const [touched, setTouched] = useState({ name: false, role: false, consent: false });
  const touch = (field: keyof typeof touched) =>
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  let nameError: string | null = null;
  if (!isSignerNameLengthValid(fullName)) nameError = labels.fullNameError;
  else if (!isSignerNameCharsetValid(fullName) || serverNameRejected) nameError = labels.fullNameCharset;
  const showNameError = nameError !== null && (forceErrors || touched.name || serverNameRejected);
  const showRoleError = (forceErrors || touched.role) && signerRole === null;
  const showConsentError = (forceErrors || touched.consent) && !consent;

  const roleOptions: { value: TermsSignerRole; label: string }[] = [
    { value: 'OWNER', label: labels.roleOwner },
    { value: 'AUTHORISED_SIGNATORY', label: labels.roleAuthorised },
  ];

  return (
    <section
      className="terms-signature-block"
      aria-labelledby={ids.title}
      data-testid="terms-signature-block"
    >
      <h4 id={ids.title} className="terms-signature-title">
        {labels.title}
      </h4>

      <div className="wizard-form-group terms-signature-field">
        <label htmlFor={ids.name}>{labels.fullName}</label>
        <input
          id={ids.name}
          type="text"
          autoComplete="name"
          dir="auto"
          spellCheck={false}
          value={fullName}
          placeholder={labels.fullNamePlaceholder}
          disabled={disabled}
          aria-required="true"
          aria-invalid={showNameError ? 'true' : undefined}
          aria-describedby={showNameError ? ids.nameError : undefined}
          onChange={(e) => onFullNameChange(e.target.value)}
          onBlur={() => touch('name')}
        />
        {showNameError && (
          <span id={ids.nameError} className="terms-signature-error" role="alert">
            {nameError}
          </span>
        )}
      </div>

      <div
        className="terms-signature-field"
        role="radiogroup"
        aria-labelledby={ids.roleLegend}
        aria-required="true"
        aria-invalid={showRoleError ? 'true' : undefined}
        aria-describedby={showRoleError ? ids.roleError : undefined}
        onBlur={(e) => {
          // Only once focus leaves the whole group, not when moving between its radios.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) touch('role');
        }}
      >
        <span id={ids.roleLegend} className="terms-signature-label">
          {labels.role}
        </span>
        <div className="vat-radio-row terms-signature-radio-row">
          {roleOptions.map((opt) => (
            <label key={opt.value} className="vat-radio-option terms-signature-option">
              <input
                type="radio"
                name={`${uid}-signer-role`}
                value={opt.value}
                checked={signerRole === opt.value}
                disabled={disabled}
                onChange={() => onSignerRoleChange(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
        {showRoleError && (
          <span id={ids.roleError} className="terms-signature-error" role="alert">
            {labels.roleError}
          </span>
        )}
      </div>

      <div className="terms-signature-field">
        <div className={`terms-signature-consent${showConsentError ? ' has-error' : ''}`}>
          <input
            id={ids.consent}
            type="checkbox"
            checked={consent}
            disabled={disabled}
            aria-required="true"
            aria-invalid={showConsentError ? 'true' : undefined}
            aria-describedby={showConsentError ? ids.consentError : undefined}
            onChange={(e) => {
              onConsentChange(e.target.checked);
              if (!e.target.checked) touch('consent');
            }}
            onBlur={() => touch('consent')}
          />
          <label htmlFor={ids.consent} dir="auto">
            {consentText}
          </label>
        </div>
        {showConsentError && (
          <span id={ids.consentError} className="terms-signature-error" role="alert">
            {labels.consentError}
          </span>
        )}
      </div>

      {labels.emailNote && (
        <p id={ids.note} className="terms-signature-note">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          <span>{labels.emailNote}</span>
        </p>
      )}
    </section>
  );
}
